import { type ComponentProps, useState } from 'react'

import { useDashboardFeatureFlags, usePlatformFeatureFlags } from '@components'
import { type ProductBox } from '@fourthwall/module-product'
import { useTagManager } from '@hooks'

import { useCurrentShop } from '../../../hooks/useCurrentShop'
import { CATEGORIES } from '../categories'
import { useHardcodedRecommendations } from '../queries/useHardcodedRecommendations'
import { usePersonalizedRecommendationsQuery } from '../queries/usePersonalizedRecommendationsQuery'
import { useProductsQuery } from '../queries/useProductsQuery'
import { removeMinorAttributes } from './utils'

export const staleTime = Infinity // each response gives different results and it crashes the slider

export const useHomeProducts = () => {
  const [activeCategory, setActiveCategory] = useState<'recommendations' | 'favorites'>(
    'recommendations',
  )
  const { hasFeatureFlag, featureFlagsQuery } = usePlatformFeatureFlags()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const TagManager = useTagManager()

  const { currentShop } = useCurrentShop()

  const { personalizedRecommendationsQuery } = usePersonalizedRecommendationsQuery(undefined, {
    staleTime,
    enabled:
      featureFlagsQuery.isFetched && !hasFeatureFlag('dashboard:recommended_products_experiment'),
  })

  const { hardcodedProductsQuery } = useHardcodedRecommendations({
    enabled:
      featureFlagsQuery.isFetched && hasFeatureFlag('dashboard:recommended_products_experiment'),
  })

  const { productsQuery } = useProductsQuery([{ favourite: true }], { staleTime })

  const recommendedProducts = hasFeatureFlag('dashboard:recommended_products_experiment')
    ? hardcodedProductsQuery.data?.products
    : personalizedRecommendationsQuery.data

  if (!dashboardFeatureFlags.homeProducts.enabled) return null

  if (!recommendedProducts?.length) return null

  if (!productsQuery.isSuccess) {
    return null
  }

  const productsPerCategory: Record<
    typeof activeCategory,
    ComponentProps<typeof ProductBox>['product'][]
  > = {
    recommendations: recommendedProducts.map(removeMinorAttributes),
    favorites: productsQuery.data.products.map(removeMinorAttributes),
  }

  const categories = {
    options: CATEGORIES,
    value: activeCategory,
    onChange: setActiveCategory,
    visible: productsPerCategory.favorites.length > 0,
  }

  const onProductClick = (product: { id: string; name: string }) => {
    if (!currentShop) return

    TagManager.trigger('recommendation_click', {
      recommendation_type: 'create_new_product',
      flow: 'product',
      product_id: product.id,
      product_name: product.name,
      shop_id: currentShop.id,
      experiment_variant: hasFeatureFlag('dashboard:recommended_products_experiment')
        ? 'ACTIVE'
        : 'NONE',
      active_category: activeCategory,
    })
  }

  return {
    categories,
    activeCategory,
    products: productsPerCategory[activeCategory],
    isLoading:
      personalizedRecommendationsQuery.isInitialLoading || hardcodedProductsQuery.isInitialLoading,
    onProductClick,
  }
}
